'use strict';

var assign = require('lodash.assign');
var omit = require('lodash.omit');
var mkdom = require('mkdom');
var toWords = require('spelled-number').toWords;

var MINUTE = 60000; // 60 * 1000
var MINUTES = MINUTE;
var HOUR = 60 * MINUTES;
var HOURS = HOUR;
var DAY = 24 * HOURS;
var DAYS = DAY;

function minutes(amount) {
  return amount * MINUTES;
}

function hours(amount) {
  return amount * HOURS;
}

function days(amount) {
  return amount * DAYS;
}

function createLocationObject(job, query, locationField) {
  var name = job[locationField];
  var result = {
    name: name
  };
  var locationWhereValueField = locationField + 'WhereValue';

  if (job[locationWhereValueField]) {
    result.title = 'Limit results to ' + name;
    result.params = assign(omit(query, 'page'), {
      where: job[locationWhereValueField]
    });
  }

  return result;
}

function getLocation(job, query) {
  if (job.suburb && job.suburb !== 'none') {
    return {
      suburb: createLocationObject(job, query, 'suburb')
    };
  }

  if (!job.location) {
    return null;
  }

  var locationObj = createLocationObject(job, query, 'location');

  if (!job.area) {
    return {
      location: locationObj
    };
  }

  return {
    location: locationObj,
    area: createLocationObject(job, query, 'area')
  };
}

function getClassification(job, query, location) {
  var classification = job.classification;
  var subClassification = job.subClassification;
  var matchedWhere = !location || location.description === 'All Australia' || location.description === 'All New Zealand' ? '' : location.description;
  var result = {};

  if (classification) {
    // should always exist, but it's safer to guard anyway
    result.classification = {
      name: classification.description,
      title: 'Limit results to ' + classification.description,
      params: assign(omit(query, 'page', 'subclassification'), {
        where: matchedWhere,
        classification: classification.id
      })
    };
  }

  if (subClassification) {
    // it is null for graduate jobs (don't ask me why)
    result.subClassification = {
      name: subClassification.description,
      title: 'Limit results to ' + subClassification.description,
      params: assign(omit(query, 'page'), {
        where: matchedWhere,
        classification: classification.id,
        subclassification: subClassification.id
      })
    };
  }

  return result;
}

function diffFromNow(dateStr, nowStr) {
  var date = new Date(dateStr);
  var now = nowStr ? new Date(nowStr) : new Date();

  return now - date;
}

// Converts:
//     5, 'minute'
// to:
//     'five minutes'
function convertToWords(number, word) {
  var numberAsWords = toWords(number);

  return numberAsWords + ' ' + word + (number === 1 ? '' : 's');
}

function getListingDate(job, now) {
  var diff = diffFromNow(job.listingDate, now);
  var number;

  if (diff < minutes(5.5)) {
    number = 5;

    return {
      shortDescription: number + 'm ago',
      longDescription: 'Listed ' + convertToWords(number, 'minute') + ' ago'
    };
  }

  if (diff < minutes(59.5)) {
    number = Math.round(diff / MINUTE);

    return {
      shortDescription: number + 'm ago',
      longDescription: 'Listed ' + convertToWords(number, 'minute') + ' ago'
    };
  }

  if (diff < hours(23.5)) {
    number = Math.round(diff / HOUR);

    return {
      shortDescription: number + 'h ago',
      longDescription: 'Listed ' + convertToWords(number, 'hour') + ' ago'
    };
  }

  if (diff < days(30.5)) {
    number = Math.round(diff / DAY);

    return {
      shortDescription: number + 'd ago',
      longDescription: 'Listed ' + convertToWords(number, 'day') + ' ago'
    };
  }

  number = 30;

  return {
    shortDescription: number + 'd+ ago',
    longDescription: 'Listed more than ' + convertToWords(number, 'day') + ' ago'
  };
}

function getIsFresh(job, now) {
  return diffFromNow(job.listingDate, now) < hours(23.5);
}

function getBulletPoints(job) {
  return job.bulletPoints && job.bulletPoints.length > 0 ? job.bulletPoints : null;
}

function getTextContentFromHtml(html) {
  return mkdom('<div>' + html + '</div>').textContent;
}

function getSchema(job, query, baseUrl) {
  if (!job.advertiser || !job.classification) {
    return null;
  }

  var location = getLocation(job, query);

  if (location === null) {
    return null;
  }

  var addressLocality = location.suburb && location.suburb.name || location.area && location.area.name;

  return {
    '@context': 'http://schema.org',
    '@type': 'JobPosting',
    datePosted: job.listingDate,
    description: getTextContentFromHtml(job.teaser),
    employmentType: job.workType,
    hiringOrganization: {
      '@type': 'Organization',
      name: job.advertiser.description
    },
    industry: job.classification.description,
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: addressLocality,
        addressRegion: job.location
      }
    },
    title: getTextContentFromHtml(job.title),
    url: baseUrl + '/job/' + job.id
  };
}

module.exports = function (data) {
  var job = data.job;
  var query = data.query || {};
  var location = data.location || null;
  var now = data.now; // used in tests to mock the current time
  var locale = data.locale || 'AU';
  var baseUrl = locale.match(/nz/i) ? 'https://www.seek.co.nz' : 'https://www.seek.com.au';
  var viewModel = {
    id: job.id,
    isPremium: job.isPremium,
    isStandOut: !job.isPremium && job.isStandOut,
    title: job.title,
    location: getLocation(job, query),
    isFresh: getIsFresh(job, now),
    teaser: job.teaser,
    workType: job.workType,
    advertiser: job.advertiser,
    classification: getClassification(job, query, location),
    locationMatch: job.locationMatch,
    bulletPoints: getBulletPoints(job),
    salary: job.salary,
    logo: job.logo,
    schema: getSchema(job, query, baseUrl),
    queryString: job.queryString,
    joraClickTrackingUrl: job.joraClickTrackingUrl,
    joraImpressionTrackingUrl: job.joraImpressionTrackingUrl
  };

  if (!job.isPremium) {
    var listingDate = getListingDate(job, now);

    viewModel.listingDate = listingDate.shortDescription;
    viewModel.listingDateDescription = listingDate.longDescription;
  }

  return viewModel;
};


/*****************
 ** WEBPACK FOOTER
 ** ./~/@seek/seek-refine-job-search/dist/view-models/job/job.js
 ** module id = 474
 ** module chunks = 0
 **/